import axios, { AxiosError } from 'axios'
import { log } from 'src/services/app/log.service'
import {
  ANALYTICS_EVENTS,
  API_ERRORS,
  ENDPOINTS_OUTSIDE_VUE_QUERY,
  ERROR_TYPES,
} from 'src/services/consts/api.const'
import { notifyMsgs, notifyService } from 'src/services/app/notify.service'
import { router } from 'src/router'
import { ROUTES } from 'src/router/const'
import { useAuthStore } from 'stores/auth'
import { useAccountStore } from 'stores/account'
import { resetStores } from 'stores/reset.store'
import { usePostHog } from 'src/composables/app/ui/usePostHog'
import { hasPracticeId } from 'src/composables/onboarding/practice/practiceMode'
import { sanitizePayload } from 'src/services/helpers/security.helper'
import { getIdTokenDetails } from 'src/services/helpers/auth.helper'
import { apiUrl, authApiUrl } from 'src/config'

let _authErrorInProgress = false
const { captureException } = usePostHog()

const httpClient = axios.create({
  timeout: 30000,
  withCredentials: false,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor - blocks walkthrough example ids, then attaches auth headers
httpClient.interceptors.request.use(
  async (config) => {
    // Example ids never reach the API. Reject before the token call: no refresh, no retry, no report.
    if (hasPracticeId(config?.url, config?.data, config?.params)) {
      const method = config.method?.toUpperCase() || 'UNKNOWN'
      const url = config.url || 'unknown-endpoint'
      const error = new Error(
        `[${API_ERRORS.PRACTICE_ID_BLOCKED}] ${method} ${url} - walkthrough example id`
      )
      error.name = API_ERRORS.PRACTICE_ID_BLOCKED
      error.config = config
      log.info('[HTTP SERVICE] Request with a walkthrough example id blocked')
      throw error
    }

    const isTrustedDomain = _isTrustedApiDomain(config.url)

    // Only add auth headers and account data to trusted internal API domains
    if (!isTrustedDomain) {
      return config
    }

    const isFormData = config.data instanceof FormData
    const authStore = useAuthStore()
    const accountStore = useAccountStore()
    const { userSub } = authStore.getAuthData()

    let idToken
    try {
      // Auth store: cache-first id_token, forces Auth0 silent refresh when the
      // id_token itself is near expiry (SDK only keys off access-token lifetime).
      idToken = await authStore.getIdToken()
    } catch (err) {
      log.error('Token acquisition failed - session expired', err)
      const error = new Error('Session expired - token refresh failed')
      error.name = API_ERRORS.AUTH_EXPIRED
      error.config = config
      error.authReason = err?.authReason || 'refresh_failed'
      error.auth0Code = err?.error
      throw error
    }

    if (!idToken) {
      const error = new Error('Session expired - no ID token in token response')
      error.name = API_ERRORS.AUTH_EXPIRED
      error.config = config
      error.authReason = 'no_id_token'
      throw error
    }

    config.headers = {
      ...config.headers,
      Authorization: `Bearer ${idToken}`,
      'X-User-ID': userSub.value || '',
    }

    if (isFormData) {
      if (accountStore.accountId) {
        config.data.set('account_id', accountStore.accountId)
      }
      delete config.headers['Content-Type']
    } else {
      if (accountStore.accountId) {
        config.data.account_id = accountStore.accountId
      }
    }

    return config
  },
  (error) => {
    log.error('[HTTP SERVICE] - request interceptor - request error', error)
    return Promise.reject(error)
  }
)

// Response interceptor
httpClient.interceptors.response.use(
  (response) => {
    const isFormData = response.config?.data instanceof FormData
    const url = response.config?.url || ''
    const endpoint = url.split('/').pop() || url
    const sanitizedRequest = isFormData ? '[FormData]' : sanitizePayload(response.config?.data)
    const sanitizedResponse = sanitizePayload(response.data)
    log.info(`API: ${endpoint}`, {
      url,
      method: response.config?.method,
      request: sanitizedRequest,
      response: sanitizedResponse,
    })

    // Check for error field in successful responses
    if (response.data && response.data.error) {
      const error = new Error(response.data.error)
      error.response = response
      error.config = response.config
      reportHttpError(error)
      return Promise.reject(error)
    }

    return response.data
  },
  async (error) => {
    // Thrown by the request interceptor above. Pass it through with no report and no sign-out.
    if (error?.name === API_ERRORS.PRACTICE_ID_BLOCKED) {
      return Promise.reject(error)
    }

    const isReportedByQueryCache =
      isNoResponseError(error) && !ENDPOINTS_OUTSIDE_VUE_QUERY.includes(error.config?.url)
    if (!isReportedByQueryCache) reportHttpError(error)

    if (error.name === API_ERRORS.AUTH_EXPIRED) {
      _handleAuthError({
        showSessionExpired: true,
        event: ANALYTICS_EVENTS.SIGNED_OUT_COULD_NOT_REFRESH,
        details: {
          reason: error.authReason,
          code: error.auth0Code,
          ..._requestDetails(error.config),
        },
      })
      return Promise.reject(error)
    }

    const { response } = error

    if (response) {
      switch (response.status) {
        case 401:
          _handleAuthError({
            showSessionExpired: response.data?.error === 'Unauthorized',
            event: ANALYTICS_EVENTS.SIGNED_OUT_SERVER_REJECTED,
            details: {
              ..._requestDetails(error.config),
              status: 401,
              detail: _responseErrorDetail(response),
              ...getIdTokenDetails(_requestBearerToken(error.config)),
            },
          })
          break

        case 403:
          notifyService.error(notifyMsgs.unauthorized, 'permission-error')
          router.push({ name: ROUTES.HOME })
          break

        default:
          break
      }
    }

    return Promise.reject(error)
  }
)

// API methods. Body defaults to {} so the request interceptor can set account_id on it.
export const httpService = {
  get: (url, config = {}) => httpClient.get(url, config),
  post: (url, data = {}, config = {}) => httpClient.post(url, data, config),
  put: (url, data = {}, config = {}) => httpClient.put(url, data, config),
  delete: (url, config = {}) => httpClient.delete(url, config),
  patch: (url, data = {}, config = {}) => httpClient.patch(url, data, config),
}

// ==================== ERROR REPORTING ====================
// Server-answered errors and AUTH_EXPIRED are final on the first attempt, so the response
// interceptor reports them. No-response errors (network/timeout/CORS) may recover on retry,
// so the Vue Query cache handlers report them once after the final attempt. Exception:
// ENDPOINTS_OUTSIDE_VUE_QUERY are reported by the interceptor because no cache handler sees them.

export function reportHttpError(error) {
  _captureApiError(error)
  _captureRequestFlow(error)
}

export function isNoResponseError(error) {
  return !!(
    error?.isAxiosError &&
    !error.response &&
    error.name !== API_ERRORS.AUTH_EXPIRED &&
    error.code !== AxiosError.ERR_CANCELED
  )
}

/**
 * Any error produced by this service: axios errors, request-interceptor AUTH_EXPIRED,
 * `body.error` responses rejected by the response interceptor, and the walkthrough example-id
 * block (PRACTICE_ID_BLOCKED), which is never reported.
 */
export function isHttpServiceError(error) {
  return !!(
    error?.isAxiosError ||
    error?.name === API_ERRORS.AUTH_EXPIRED ||
    error?.name === API_ERRORS.PRACTICE_ID_BLOCKED ||
    error?.response
  )
}

// ==================== PRIVATE HELPERS ====================

/**
 * Auth-flow event for a failed request to our own API, so a sign-out can be
 * lined up with the server errors or dead connections that preceded it.
 * 401 and AUTH_EXPIRED are covered by the sign-out events; cancelled requests are skipped.
 * Unlike _captureApiError, offline failures are reported (`online: false`).
 */
function _captureRequestFlow(error) {
  const { response, config } = error
  if (!_isTrustedApiDomain(config?.url)) return
  if (
    error.name === API_ERRORS.AUTH_EXPIRED ||
    response?.status === 401 ||
    error.code === AxiosError.ERR_CANCELED
  ) {
    return
  }

  const authStore = useAuthStore()
  if (response) {
    authStore.captureAuthFlow(ANALYTICS_EVENTS.SERVER_RETURNED_AN_ERROR, {
      ..._requestDetails(config),
      status: response.status,
      detail: _responseErrorDetail(response),
    })
    return
  }

  authStore.captureAuthFlow(ANALYTICS_EVENTS.SERVER_DID_NOT_RESPOND, {
    ..._requestDetails(config),
    code: error.code || 'unknown',
    online: navigator.onLine,
  })
}

/**
 * Capture API error to PostHog with readable, structured names.
 *
 * Skipped when the browser is offline (navigator.onLine === false).
 *
 * PostHog UI title  = apiError.name: apiError.message  (from the Error object)
 * Slack properties  = error_name, original_error_message, error_context  (custom props)
 *
 * Error message format uses bracket prefixes for at-a-glance context:
 *   [Authentication Expired] - No ID token, session expired before request
 *   [API Request Error]      - Server responded; HTTP status is in parentheses, e.g. (401)
 *   [error.code]             - No response; bracket is the Axios code
 *                              (ECONNABORTED, ERR_NETWORK, ERR_CANCELED, …)
 *   [Network Error]          - No response and no error code
 */
function _captureApiError(error) {
  const { response, config } = error
  const method = config?.method?.toUpperCase() || 'UNKNOWN'
  const url = config?.url || 'unknown-endpoint'

  if (!navigator.onLine) return

  const isAuth = error.name === API_ERRORS.AUTH_EXPIRED || response?.status === 401
  let errorMessage

  if (error.name === API_ERRORS.AUTH_EXPIRED) {
    errorMessage = `[${API_ERRORS.AUTH_EXPIRED}] No ID token available [${method} ${url}]`
  } else if (response) {
    const detail = response.data?.error || response.statusText || 'Server error'
    errorMessage = `[${API_ERRORS.REQ_ERROR}] ${method} ${url} - ${detail} (${response.status})`
  } else {
    const label = error.code || API_ERRORS.NETWORK_ERROR
    errorMessage = `[${label}] ${method} ${url} - No response received`
  }

  captureException(error, {
    error_type: isAuth ? ERROR_TYPES.AUTH : ERROR_TYPES.API,
    error_name: errorMessage,
    original_error_message: error.message,
    error_context: `${method} ${url}`,
    request_url: config?.url,
    request_method: config?.method,
    request_payload: sanitizePayload(config?.data, true),
    error_code: error.code,
    response_status: response?.status,
    error_data: sanitizePayload(response?.data, true),
  })
}

async function _handleAuthError({
  showSessionExpired = false,
  event = ANALYTICS_EVENTS.SIGNED_OUT_SERVER_REJECTED,
  details,
} = {}) {
  if (_authErrorInProgress) return
  _authErrorInProgress = true

  const authStore = useAuthStore()
  try {
    await authStore.logout({ authError: true, event, details })
  } catch (err) {
    log.error('Failed to logout properly', err)
    resetStores()
    router.push({ path: '/not-found' })
  } finally {
    _authErrorInProgress = false
  }

  if (showSessionExpired) {
    notifyService.error(notifyMsgs.sessionExpired, 'auth-error')
  }
}

function _requestPath(url) {
  if (!url || typeof url !== 'string') return undefined
  try {
    return new URL(url, window.location.origin).pathname
  } catch {
    return undefined
  }
}

function _requestDetails(config) {
  return {
    path: _requestPath(config?.url),
    method: config?.method?.toUpperCase(),
  }
}

function _responseErrorDetail(response) {
  const detail = response?.data?.error
  return typeof detail === 'string' ? detail : undefined
}

function _requestBearerToken(config) {
  const authorization = config?.headers?.get?.('Authorization') ?? config?.headers?.Authorization
  return typeof authorization === 'string' ? authorization.replace(/^Bearer /, '') : undefined
}

/**
 * Check if URL belongs to a trusted internal API domain.
 * Prevents leaking auth headers to external/third-party services.
 */
function _isTrustedApiDomain(url) {
  if (!url) return false

  // Relative URLs are trusted (same origin), but exclude protocol-relative URLs (//evil.com)
  if (url.startsWith('/') && !url.startsWith('//')) return true

  try {
    const TRUSTED_API_ORIGINS = [apiUrl, authApiUrl].filter(Boolean).map((url) => {
      try {
        return new URL(url).origin
      } catch {
        return url
      }
    })
    const requestOrigin = new URL(url).origin
    return TRUSTED_API_ORIGINS.includes(requestOrigin)
  } catch {
    // If URL parsing fails, treat as untrusted
    return false
  }
}
